import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useNavigate } from "react-router-dom";
import { getParticipantAttendanceHistory, getProjectAttendance, getProjectDesignationOptions, getProjectSupervisorOptions } from "../services/attendanceApi";
import "./Attendance.css";

const PAGE_SIZES = [10, 25, 50, 100];
const NFC_OPTIONS = [
  { value: "", label: "All NFC Status" },
  { value: "true", label: "NFC Configured" },
  { value: "false", label: "NFC Not Configured" },
];

const selectedProjectId = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("selectedProject") || "null");
    return String(stored?.project_id || stored?.id || stored || "").trim();
  } catch {
    return "";
  }
};

const formatTime = (value, timeZone) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString([], {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  });
};

const formatLocation = (location) => {
  if (!location || location.latitude == null || location.longitude == null) return "—";
  return `${Number(location.latitude).toFixed(5)}, ${Number(location.longitude).toFixed(5)}`;
};

const formatDuration = (minutes) => {
  if (minutes == null) return "—";
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

const checkoutReasonLabel = (reason, status) => {
  if (reason === "AUTO_CHECK_OUT") return "Auto checkout";
  if (reason === "HANDOFF") return "Supervisor handoff";
  return status === "OUT" ? "Checked out" : "";
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
};

const historyDateValue = (date) => {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 10);
};

const defaultHistoryDates = () => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  return { fromDate: historyDateValue(yesterday), toDate: historyDateValue(today) };
};

const errorMessageFor = (error) => {
  const status = error?.response?.status;
  if (status === 403) return "You do not have access to this project.";
  if (status === 404) return "Project not found.";
  return error?.response?.data?.message || "Unable to load participant attendance.";
};

export default function Attendance({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(selectedProjectId);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [supervisorName, setSupervisorName] = useState("");
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [designation, setDesignation] = useState("");
  const [designationOptions, setDesignationOptions] = useState([]);
  const [attendanceStatus, setAttendanceStatus] = useState("");
  const [nfcConfigured, setNfcConfigured] = useState("");
  const [scannedByMe, setScannedByMe] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [response, setResponse] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [error, setError] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);
  const [historyParticipant, setHistoryParticipant] = useState(null);
  const [historyResponse, setHistoryResponse] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [historyDates, setHistoryDates] = useState(defaultHistoryDates);
  const requestSequence = useRef(0);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = searchInput.trim();
      setSearch((current) => {
        if (current === next) return current;
        setPage(1);
        return next;
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const handleProjectChange = () => {
      setProjectId(selectedProjectId());
      setSupervisorName("");
      setDesignation("");
      setPage(1);
      setResponse(null);
      hasLoadedRef.current = false;
    };
    window.addEventListener("projectChanged", handleProjectChange);
    return () => window.removeEventListener("projectChanged", handleProjectChange);
  }, []);

  useEffect(() => {
    if (!projectId) {
      setSupervisorOptions([]);
      setDesignationOptions([]);
      return undefined;
    }

    const controller = new AbortController();
    getProjectSupervisorOptions(projectId, controller.signal)
      .then((users) => {
        const names = [...new Set(users.map((user) => String(user?.user_name || "").trim()).filter(Boolean))]
          .sort((first, second) => first.localeCompare(second));
        setSupervisorOptions(names);
      })
      .catch((requestError) => {
        if (requestError?.code !== "ERR_CANCELED") setSupervisorOptions([]);
      });

    getProjectDesignationOptions(projectId, controller.signal)
      .then((values) => setDesignationOptions(values))
      .catch((requestError) => {
        if (requestError?.code !== "ERR_CANCELED") setDesignationOptions([]);
      });

    return () => controller.abort();
  }, [projectId]);

  const query = useMemo(() => ({
    page,
    pageSize,
    search,
    supervisorName,
    designation,
    attendanceStatus,
    nfcConfigured,
    scannedByMe,
  }), [page, pageSize, search, supervisorName, designation, attendanceStatus, nfcConfigured, scannedByMe]);

  const loadAttendance = useCallback(() => {
    if (!projectId) {
      setInitialLoading(false);
      setError("Select a project to view participant attendance.");
      return undefined;
    }

    const sequence = ++requestSequence.current;
    const controller = new AbortController();
    hasLoadedRef.current ? setRefetching(true) : setInitialLoading(true);
    setError("");

    getProjectAttendance(projectId, query, controller.signal)
      .then((data) => {
        if (sequence !== requestSequence.current) return;
        setResponse({ ...data, data: Array.isArray(data.data) ? data.data : [] });
        hasLoadedRef.current = true;
      })
      .catch((requestError) => {
        if (requestError?.code !== "ERR_CANCELED" && sequence === requestSequence.current) {
          setError(errorMessageFor(requestError));
        }
      })
      .finally(() => {
        if (sequence === requestSequence.current) {
          setInitialLoading(false);
          setRefetching(false);
        }
      });

    return () => controller.abort();
  }, [projectId, query, refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => loadAttendance(), [loadAttendance]);

  useEffect(() => {
    if (!historyParticipant || !projectId) return undefined;
    const controller = new AbortController();
    setHistoryLoading(true);
    setHistoryError("");
    getParticipantAttendanceHistory(projectId, historyParticipant.participant_id, { page: historyPage, ...historyDates }, controller.signal)
      .then(setHistoryResponse)
      .catch((requestError) => {
        if (requestError?.code !== "ERR_CANCELED") setHistoryError(requestError?.response?.data?.message || "Unable to load scan history.");
      })
      .finally(() => setHistoryLoading(false));
    return () => controller.abort();
  }, [historyParticipant, projectId, historyPage, historyDates]);

  const rows = response?.data || [];
  const summary = response?.summary || {};
  const pagination = response?.pagination || {
    page,
    page_size: pageSize,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false,
  };

  const applyStatus = (status) => {
    setAttendanceStatus(status);
    setPage(1);
  };

  const hasActiveFilters = Boolean(searchInput || supervisorName || designation || nfcConfigured || scannedByMe || attendanceStatus);
  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setSupervisorName("");
    setDesignation("");
    setNfcConfigured("");
    setScannedByMe(false);
    setAttendanceStatus("");
    setPage(1);
  };

  return (
    <div className={`table-container attendance-page ${isSidebarExpanded ? "expanded" : "collapsed"}`}>
      <div className="attendance-content">
        <header className="attendance-header">
          <div className="attendance-heading">
            <h2>Attendance</h2>
            <p>Today · {response?.project?.local_date || "—"} · {response?.project?.timezone || "—"}</p>
          </div>
          <div className="attendance-header-actions">
            <label className="attendance-switch">
              <Switch
                size="small"
                checked={scannedByMe}
                onChange={(event) => { setScannedByMe(event.target.checked); setPage(1); }}
              />
              <span>Scanned by Me</span>
            </label>
            <Button className="attendance-heatmap-link" startIcon={<MapOutlinedIcon />} onClick={() => navigate(`/attendance/heatmap/${encodeURIComponent(projectId)}`)} disabled={!projectId}>Heatmap</Button>
            <Tooltip title="Refresh">
              <IconButton
                className={`attendance-toolbar-refresh ${refetching ? "is-loading" : ""}`}
                aria-label="Refresh participant attendance"
                onClick={() => setRefreshToken((value) => value + 1)}
                disabled={initialLoading || refetching}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {hasActiveFilters && <Button className="attendance-reset-filters" onClick={resetFilters}>Reset</Button>}
          </div>
        </header>

        <section className="attendance-toolbar" aria-label="Participant attendance filters">
            <TextField
              className="attendance-search"
              size="small"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search participants or NFC UID"
              inputProps={{ "aria-label": "Search participants" }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
          <Autocomplete
            className="attendance-filter-input attendance-supervisor-filter"
            options={["", ...supervisorOptions]}
            value={supervisorName}
            size="small"
            autoHighlight
            clearOnEscape
            openOnFocus
            noOptionsText="No supervisors found"
            getOptionLabel={(option) => option || "All Supervisors"}
            isOptionEqualToValue={(option, value) => option === value}
            onChange={(_, value) => { setSupervisorName(value || ""); setPage(1); }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <span className="attendance-supervisor-option-name">{option || "All Supervisors"}</span>
                {selected && <CheckIcon className="attendance-supervisor-option-check" fontSize="small" />}
              </li>
            )}
            slotProps={{
              paper: { className: "attendance-supervisor-popper-paper" },
              listbox: { className: "attendance-supervisor-options" },
              popper: { className: "attendance-supervisor-popper" },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Supervisor name"
                inputProps={{ ...params.inputProps, "aria-label": "Filter by supervisor name" }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <>
                    <Tooltip title="Shows participants last scanned by this supervisor.">
                      <IconButton className="attendance-supervisor-info" aria-label="Supervisor filter information" size="small">
                        <InfoOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    {params.InputProps.endAdornment}
                  </>,
                }}
              />
            )}
          />
          <Autocomplete
            className="attendance-filter-input attendance-designation-filter"
            options={["", ...designationOptions]}
            value={designation}
            size="small"
            autoHighlight
            clearOnEscape
            openOnFocus
            noOptionsText="No designations found"
            getOptionLabel={(option) => option || "All Designations"}
            isOptionEqualToValue={(option, value) => option === value}
            onChange={(_, value) => { setDesignation(value || ""); setPage(1); }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <span className="attendance-supervisor-option-name">{option || "All Designations"}</span>
                {selected && <CheckIcon className="attendance-supervisor-option-check" fontSize="small" />}
              </li>
            )}
            slotProps={{
              paper: { className: "attendance-supervisor-popper-paper" },
              listbox: { className: "attendance-supervisor-options" },
              popper: { className: "attendance-supervisor-popper" },
            }}
            renderInput={(params) => <TextField {...params} placeholder="Designation" inputProps={{ ...params.inputProps, "aria-label": "Filter by designation" }} />}
          />
            <FormControl className="attendance-filter" size="small">
              <Select
                value={nfcConfigured}
                displayEmpty
                inputProps={{ "aria-label": "NFC status" }}
                onChange={(event) => { setNfcConfigured(event.target.value); setPage(1); }}
              >
                {NFC_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
        </section>

        <section className="attendance-tabs" role="tablist" aria-label="Participant scan status">
          {[
          ["", "All", summary.total_participants],
            ["WITH_ME", "With Me", summary.with_me],
            ["WITH_OTHER_SUPERVISOR", "With Other Supervisor", summary.with_other_supervisor],
            ["CHECKED_OUT", "Checked Out", summary.checked_out],
            ["SCANNED", "Scanned", summary.scanned],
            ["NOT_SCANNED", "Not Scanned", summary.not_scanned],
          ].map(([status, label, count]) => (
            <button
              key={label}
              type="button"
              role="tab"
              aria-selected={attendanceStatus === status}
              className={`attendance-tab${attendanceStatus === status ? " active" : ""}`}
              onClick={() => applyStatus(status)}
            >
              <span className="attendance-tab-label">{label}</span>
              <span className="attendance-tab-count">{count ?? 0}</span>
            </button>
          ))}
        </section>

        {error && <Alert severity="error" className="attendance-error" action={
          <Button color="inherit" size="small" onClick={() => setRefreshToken((value) => value + 1)}>Retry</Button>
        }>{error}</Alert>}

        <section className="attendance-table-card">
          {refetching && <div className="attendance-loading-overlay"><span /></div>}
          <div className="attendance-table-scroll">
            <Table stickyHeader size="small" className="attendance-table">
              <TableHead><TableRow>
                <TableCell>SR. NO.</TableCell>
                <TableCell>Participant</TableCell>
                <TableCell>Designation</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Attendance Timeline</TableCell>
                <TableCell>Last Scan Details</TableCell>
                <TableCell>NFC</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {initialLoading && Array.from({ length: 8 }, (_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {Array.from({ length: 8 }, (__, cell) => <TableCell key={cell}><Skeleton height={24} /></TableCell>)}
                  </TableRow>
                ))}
                {!initialLoading && !error && rows.map((row, index) => (
                  <TableRow hover key={row.participant_id}>
                    <TableCell>{(pagination.page - 1) * pagination.page_size + index + 1}</TableCell>
                    <TableCell><div className="attendance-employee"><strong>{row.name || "—"}</strong><span>{row.nfc?.masked_uid || "NFC not configured"}</span></div></TableCell>
                    <TableCell>{row.designation || "—"}</TableCell>
                    <TableCell><span className={`attendance-status ${String(row.attendance_status || "").toLowerCase()}`}>{({ WITH_ME: "With Me", WITH_OTHER_SUPERVISOR: "With Other Supervisor", CHECKED_OUT: "Checked Out", SCANNED: "Scanned", NOT_SCANNED: "Not Scanned" })[row.attendance_status] || "Not Scanned"}</span></TableCell>
                    <TableCell><div className="attendance-timeline"><strong className={row.attendance_status === "CHECKED_OUT" ? "checked-out" : ""}>{row.attendance_status === "CHECKED_OUT" ? "Checked out" : row.last_scan?.scanned_at ? "Checked in" : "Not active today"}</strong><small>{row.attendance_status === "CHECKED_OUT" ? formatTime(row.attendance?.check_out, response?.project?.timezone) : row.last_scan?.scanned_at ? formatTime(row.last_scan.scanned_at, response?.project?.timezone) : "No participant scan recorded"}</small></div></TableCell>
                    <TableCell><div className="attendance-scan-details"><strong>{row.last_scan?.scanned_at ? "Scanned" : "Not scanned"}</strong><small>{row.last_scan?.scanned_at ? `${formatTime(row.last_scan.scanned_at, response?.project?.timezone)}${row.last_scan?.scanned_by?.name ? ` · ${row.last_scan.scanned_by.name}` : ""}` : "No scan details"}</small></div></TableCell>
                    <TableCell><span className={`attendance-nfc-status${row.nfc?.configured ? " configured" : " not-configured"}`}>{row.nfc?.configured ? "Configured" : "Not Configured"}</span></TableCell>
                    <TableCell className="attendance-actions-cell"><Tooltip title="View attendance history"><IconButton className="attendance-history-trigger" aria-label={`View ${row.name || "participant"} attendance history`} onClick={() => { setHistoryParticipant(row); setHistoryDates(defaultHistoryDates()); setHistoryPage(1); setHistoryResponse(null); }}><HistoryIcon /></IconButton></Tooltip></TableCell>
                  </TableRow>
                ))}
                {!initialLoading && !error && rows.length === 0 && (
                  <TableRow><TableCell colSpan={8}>
                    <div className="attendance-empty"><strong>No participants found</strong><span>Try a different search or filter.</span></div>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <footer className="attendance-pagination">
            <div className="attendance-total-records">Total Records: <strong>{pagination.total_records}</strong></div>
            <div className="attendance-pagination-controls">
              <span>Per Page:</span>
              <FormControl size="small"><Select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}>
                {PAGE_SIZES.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
              </Select></FormControl>
              <IconButton aria-label="Previous page" disabled={!pagination.has_previous || refetching} onClick={() => setPage((value) => Math.max(1, value - 1))}><NavigateBeforeIcon /></IconButton>
              <span className="attendance-page-info">Page <strong>{pagination.page}</strong> of <strong>{Math.max(pagination.total_pages, 1)}</strong></span>
              <IconButton aria-label="Next page" disabled={!pagination.has_next || refetching} onClick={() => setPage((value) => value + 1)}><NavigateNextIcon /></IconButton>
            </div>
          </footer>
        </section>
        <Drawer anchor="right" open={Boolean(historyParticipant)} onClose={() => setHistoryParticipant(null)} PaperProps={{ className: "attendance-history-drawer" }}>
          <header className="attendance-history-header"><div><h3>Attendance History – {historyParticipant?.name || "Participant"}</h3><p>{response?.project?.timezone || "Project timezone"}</p></div><IconButton aria-label="Close attendance history" onClick={() => setHistoryParticipant(null)}><CloseIcon /></IconButton></header>
          <div className="attendance-history-filters"><TextField label="From Date" type="date" size="small" value={historyDates.fromDate} onChange={(event) => { setHistoryDates((value) => ({ ...value, fromDate: event.target.value })); setHistoryPage(1); }} InputLabelProps={{ shrink: true }} inputProps={{ max: historyDates.toDate }} /><TextField label="To Date" type="date" size="small" value={historyDates.toDate} onChange={(event) => { setHistoryDates((value) => ({ ...value, toDate: event.target.value })); setHistoryPage(1); }} InputLabelProps={{ shrink: true }} inputProps={{ min: historyDates.fromDate }} /><Button onClick={() => { setHistoryDates(defaultHistoryDates()); setHistoryPage(1); }}>Reset dates</Button></div>
          <div className="attendance-history-table-scroll">
            {historyLoading && <div className="attendance-history-message"><span>Loading scan history…</span></div>}
            {historyError && <div className="attendance-history-message attendance-history-error"><strong>{historyError}</strong></div>}
            {!historyLoading && !historyError && historyResponse?.data?.length === 0 && <div className="attendance-history-message"><strong>No scan history yet</strong><span>Scans for this participant will appear here.</span></div>}
            {!historyLoading && !historyError && historyResponse?.data?.length > 0 && <table className="attendance-history-table"><thead><tr><th>Check-in</th><th>Checked In By</th><th>Latest Scan</th><th>Check-out</th><th>Duration</th></tr></thead><tbody>{historyResponse.data.map((record) => <tr key={record.id}><td>{formatTime(record.check_in, response?.project?.timezone)}</td><td>{record.checked_in_by?.name || "—"}</td><td>{record.latest_scan?.scanned_at ? <><div>{formatTime(record.latest_scan.scanned_at, response?.project?.timezone)}</div><small>{record.latest_scan.scanned_by?.name || "—"}</small></> : "—"}</td><td>{record.check_out ? <><div>{formatTime(record.check_out, response?.project?.timezone)}</div><small>{checkoutReasonLabel(record.checkout_reason, record.status)}</small></> : <span className="attendance-history-active">Active</span>}</td><td>{formatDuration(record.duration_minutes)}</td></tr>)}</tbody></table>}
          </div>
          {historyResponse?.pagination?.total_pages > 1 && <footer className="attendance-history-pagination"><span>Page {historyResponse.pagination.page} of {historyResponse.pagination.total_pages}</span><div><IconButton aria-label="Previous history page" disabled={!historyResponse.pagination.has_previous} onClick={() => setHistoryPage((value) => value - 1)}><NavigateBeforeIcon /></IconButton><IconButton aria-label="Next history page" disabled={!historyResponse.pagination.has_next} onClick={() => setHistoryPage((value) => value + 1)}><NavigateNextIcon /></IconButton></div></footer>}
        </Drawer>
      </div>
    </div>
  );
}
